import { useEffect, useRef, useState } from "react";
import { ApiError, intakeChat, extractIntakeText } from "../../api";
import type {
  ExtractionResult,
  IntakeChatMessage,
  ChatContentBlock,
} from "../../types/intake";

interface Props {
  /** Fired with the extraction once "Fill in form & continue" runs. */
  onExtracted: (result: ExtractionResult) => void;
  onCancel: () => void;
  /** Arms the host's dismiss guardrail on first input. */
  onDirty?: () => void;
  /** Create → "intake" (full summary); Enrich → "call" (call-notes update). */
  noteVariant?: "intake" | "call";
  /** Enrich tab: the existing deal's current values → the conversation knows
   *  which deal it's editing (acts on corrections, no re-description). */
  dealContext?: string;
}

type UiLine = { kind: "user" | "assistant" | "trace"; text: string };

/** Build the display thread from the Anthropic-format messages. Tool calls
 *  collapse to a single "🔍 searched Outlook" trace line — no raw tool JSON. */
function deriveUiThread(messages: IntakeChatMessage[]): UiLine[] {
  const out: UiLine[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      if (typeof m.content === "string") out.push({ kind: "user", text: m.content });
      // user turns carrying tool_result blocks are machine context — not shown
    } else {
      if (typeof m.content === "string") {
        out.push({ kind: "assistant", text: m.content });
        continue;
      }
      const blocks = m.content as ChatContentBlock[];
      if (blocks.some((b) => b.type === "tool_use")) {
        out.push({ kind: "trace", text: "🔍 searched Outlook" });
      }
      const text = blocks.filter((b) => b.type === "text").map((b) => b.text ?? "").join("");
      if (text.trim()) out.push({ kind: "assistant", text });
    }
  }
  return out;
}

/** Flatten the conversation to the transcript the extractor expects (port spec §4d). */
function serializeChat(messages: IntakeChatMessage[]): string {
  const blocks: string[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      if (typeof m.content === "string") blocks.push(`[USER]\n${m.content}`);
      else
        for (const b of m.content)
          if (b.type === "tool_result")
            blocks.push(
              `[TOOL RESULT]\n${typeof b.content === "string" ? b.content : JSON.stringify(b.content)}`,
            );
    } else {
      if (typeof m.content === "string") blocks.push(`[ASSISTANT]\n${m.content}`);
      else
        for (const b of m.content) {
          if (b.type === "text") blocks.push(`[ASSISTANT]\n${b.text ?? ""}`);
          else if (b.type === "tool_use")
            blocks.push(`[ASSISTANT CALLED TOOL] ${b.name}(${JSON.stringify(b.input ?? {})})`);
        }
    }
  }
  return blocks.join("\n\n---\n\n");
}

/**
 * ChatCapture — the scoped intake conversation (its own system prompt + Outlook
 * tools, NOT the cockpit brain). Multi-turn; "Fill in form & continue" serializes
 * the transcript and runs the forced-tool extraction, handing the result up.
 */
export function ChatCapture({
  onExtracted,
  onCancel,
  onDirty,
  noteVariant = "intake",
  dealContext,
}: Props) {
  const [messages, setMessages] = useState<IntakeChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const thread = deriveUiThread(messages);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, sending]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    onDirty?.();
    const next: IntakeChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    setError(null);
    try {
      const res = await intakeChat(next, dealContext);
      setMessages(res.messages);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.code} (HTTP ${err.status})` : "chat_failed");
      // keep the user's message in the thread so nothing's lost
    } finally {
      setSending(false);
    }
  }

  async function fillForm() {
    setExtracting(true);
    setError(null);
    try {
      const result = await extractIntakeText(serializeChat(messages), "chat", noteVariant);
      onExtracted(result);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.code} (HTTP ${err.status})` : "extract_failed");
      setExtracting(false);
    }
  }

  const hasConversation = thread.some((l) => l.kind === "user");

  return (
    <div className="intake-chat">
      <div className="intake-chat-thread" ref={scrollRef}>
        {thread.length === 0 && (
          <div className="intake-chat-empty">
            Paste a borrower/broker email, describe a call, or type notes. I'll ask
            for what's missing — or say “find the email from …” and I'll search
            your Outlook.
          </div>
        )}
        {thread.map((l, i) =>
          l.kind === "trace" ? (
            <div key={i} className="intake-chat-trace">{l.text}</div>
          ) : (
            <div key={i} className={`intake-chat-bubble intake-chat-${l.kind}`}>{l.text}</div>
          ),
        )}
        {sending && (
          <div className="intake-chat-bubble intake-chat-assistant intake-chat-thinking">
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
          </div>
        )}
      </div>

      {error && <div className="write-confirm-error">⚠ {error}</div>}

      <div className="intake-chat-input-row">
        <textarea
          className="intake-textarea intake-chat-input"
          value={input}
          rows={2}
          placeholder="Paste an email or describe the deal…"
          disabled={extracting}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="intake-chat-send" onClick={() => void send()} disabled={sending || extracting || !input.trim()}>
          {sending ? "…" : "SEND"}
        </button>
      </div>

      <div className="intake-actions">
        <button className="write-confirm-cancel" onClick={onCancel} disabled={extracting}>
          CANCEL
        </button>
        <button
          className="intake-chat-fill"
          onClick={() => void fillForm()}
          disabled={!hasConversation || sending || extracting}
          title={hasConversation ? "Extract the deal and review" : "Say something first"}
        >
          {extracting ? "EXTRACTING…" : "✦ FILL IN FORM & CONTINUE"}
        </button>
      </div>
    </div>
  );
}
